/**
 * Standalone invite acceptance page (not wrapped in admin Shell).
 * Validates an invite token, then registers a passkey to complete signup.
 */

import { Input, Loader } from "@cloudflare/kumo";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { useSearch } from "@tanstack/react-router";
import * as React from "react";

import { useAdminBranding } from "../lib/admin-branding-context";
import { validateInviteToken, type InviteVerifyResult } from "../lib/api";
import { useAuthProviderList } from "../lib/auth-provider-context";
import { PasskeyRegistration } from "./auth/PasskeyRegistration";
import { BrandLogo } from "./Logo.js";
import { RouterLinkButton } from "./RouterLinkButton.js";

type InviteStep = "verify" | "register" | "error";

interface RegisterStepProps {
	inviteData: InviteVerifyResult;
	token: string;
}

function handleInviteSuccess() {
	window.location.href = "/_emdash/admin";
}

function RegisterStep({ inviteData, token }: RegisterStepProps) {
	const { t } = useLingui();
	const [name, setName] = React.useState("");
	const buttonProviders = useAuthProviderList().filter((p) => p.LoginButton);

	return (
		<div className="space-y-6">
			<div className="text-center">
				<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-kumo-brand/10 mx-auto mb-4">
					<svg
						className="w-8 h-8 text-kumo-link"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
						/>
					</svg>
				</div>
				<h2 className="text-xl font-semibold">{t`You've been invited!`}</h2>
				<p className="text-kumo-subtle mt-2">
					<Trans>
						You'll be joining as{" "}
						<span className="font-medium text-kumo-default">{inviteData.roleName}</span>
					</Trans>
				</p>
			</div>

			<Input label={t`Email`} value={inviteData.email} disabled className="bg-kumo-tint" />

			<Input
				label={t`Your name (optional)`}
				type="text"
				value={name}
				onChange={(e) => setName(e.target.value)}
				placeholder="Jane Doe"
				autoComplete="name"
				autoFocus
			/>

			<div className="pt-4 border-t">
				<h3 className="text-sm font-medium mb-3">{t`Create your passkey`}</h3>
				<p className="text-sm text-kumo-subtle mb-4">
					{t`Passkeys are a secure, passwordless way to sign in using your device's biometrics, PIN, or security key.`}
				</p>

				<PasskeyRegistration
					optionsEndpoint="/_emdash/api/auth/invite/register-options"
					verifyEndpoint="/_emdash/api/auth/invite/complete"
					onSuccess={handleInviteSuccess}
					buttonText={t`Create Account`}
					additionalData={{ token, name: name || undefined }}
				/>
			</div>

			{buttonProviders.length > 0 && (
				<>
					{/* Divider */}
					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-kumo-base px-2 text-kumo-subtle">{t`Or continue with`}</span>
						</div>
					</div>

					{/* Accept the invite via an OAuth provider. The button carries the
					    invite token; the callback only completes the invite when the
					    provider-verified email matches the invited address. */}
					<div
						className={`grid gap-3 ${buttonProviders.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
					>
						{buttonProviders.map((provider) => {
							const Btn = provider.LoginButton!;
							return (
								<div key={provider.id}>
									<Btn inviteToken={token} />
								</div>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
}

interface ErrorStepProps {
	message: string;
	code?: string;
}

function ErrorStep({ message, code }: ErrorStepProps) {
	const { t } = useLingui();
	return (
		<div className="space-y-6 text-center">
			<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-kumo-danger/10 mx-auto">
				<svg
					className="w-8 h-8 text-kumo-danger"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
					/>
				</svg>
			</div>

			<div>
				<h2 className="text-xl font-semibold text-kumo-danger">
					{code === "TOKEN_EXPIRED"
						? t`Invite expired`
						: code === "INVALID_TOKEN"
							? t`Invalid invite link`
							: code === "USER_EXISTS"
								? t`Account already exists`
								: t`Something went wrong`}
				</h2>
				<p className="text-kumo-subtle mt-2">{message}</p>
			</div>

			<div className="space-y-2">
				{code === "USER_EXISTS" ? (
					<RouterLinkButton to="/login" className="w-full">{t`Sign in instead`}</RouterLinkButton>
				) : (
					<>
						<p className="text-sm text-kumo-subtle">
							{t`Please ask your administrator to send a new invite.`}
						</p>
						<RouterLinkButton
							to="/login"
							variant="ghost"
							className="w-full"
						>{t`Back to login`}</RouterLinkButton>
					</>
				)}
			</div>
		</div>
	);
}

export function InviteAcceptPage() {
	const { t } = useLingui();
	const { logo: brandLogo, siteName: brandSiteName } = useAdminBranding();
	const { token: urlToken } = useSearch({ strict: false });
	const [step, setStep] = React.useState<InviteStep>("verify");
	const [error, setError] = React.useState<string | undefined>();
	const [errorCode, setErrorCode] = React.useState<string | undefined>();
	const [isLoading, setIsLoading] = React.useState(true);
	const [inviteData, setInviteData] = React.useState<InviteVerifyResult | null>(null);
	const [token, setToken] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!urlToken) {
			setError(t`No invite token provided`);
			setStep("error");
			setIsLoading(false);
			return;
		}

		setToken(urlToken);
		void verifyToken(urlToken);
	}, [urlToken]);

	const verifyToken = async (tokenToVerify: string) => {
		setIsLoading(true);
		setError(undefined);
		setErrorCode(undefined);

		try {
			const result = await validateInviteToken(tokenToVerify);
			setInviteData(result);
			setStep("register");
		} catch (err) {
			const verifyError = err instanceof Error ? err : new Error(String(err));
			const errorWithCode = verifyError as Error & { code?: string };
			setError(verifyError.message);
			setErrorCode(typeof errorWithCode.code === "string" ? errorWithCode.code : undefined);
			setStep("error");
		} finally {
			setIsLoading(false);
		}
	};

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-kumo-base">
				<div className="text-center">
					<Loader />
					<p className="mt-4 text-kumo-subtle">{t`Verifying your invite...`}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-kumo-base p-4">
			<div className="w-full max-w-md">
				<div className="text-center mb-8">
					<BrandLogo logoUrl={brandLogo} siteName={brandSiteName} className="h-10 mx-auto mb-2" />
					<h1 className="text-2xl font-semibold text-kumo-default">
						{step === "register" && t`Accept Invite`}
						{step === "error" && t`Invite Error`}
					</h1>
				</div>

				<div className="bg-kumo-base border rounded-lg shadow-sm p-6">
					{step === "register" && inviteData && token && (
						<RegisterStep inviteData={inviteData} token={token} />
					)}

					{step === "error" && (
						<ErrorStep message={error ?? t`An unknown error occurred`} code={errorCode} />
					)}
				</div>
			</div>
		</div>
	);
}

export default InviteAcceptPage;
